// L'assistant, côté réglages (lot 3.6) : ce qu'il sait de vous, ce qu'il a fait (le journal des actions), ce qu'il
// consomme, et le mode de débogage de 24 heures. Tout ce qu'Alfred fait doit pouvoir se relire ici.

import { useCallback, useEffect, useState } from 'react';

import type { Moi } from '@alfred/shared';
import type { Actions } from '../App';
import { EnTeteRetour, Fenetre } from '../components';
import { type EtatIA, type LigneJournal, NonConnecte, type TableauEntretien, api } from '../lib/api';

function echecDe(actions: Actions) {
  return (e: unknown) => {
    if (e instanceof NonConnecte) window.location.hash = '#/connexion';
    else actions.toast({ texte: (e as Error).message, erreur: true });
  };
}

const quand = (iso: string) => new Date(iso).toLocaleString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function Assistant({ moi, actions }: { moi: Moi; actions: Actions }) {
  const [etat, setEtat] = useState<EtatIA | null>(null);
  const [entretien, setEntretien] = useState<TableauEntretien | null>(null);
  const echec = useCallback(echecDe(actions), [actions]);

  const charger = useCallback(() => {
    api.etatIA().then(setEtat).catch(echec);
    api.entretien().then(setEntretien).catch(() => undefined);
  }, [echec]);
  useEffect(charger, [charger]);

  const debogage = async (actif: boolean) => {
    try {
      await api.reglerDebogage(actif);
      actions.toast({ texte: actif ? 'Mode de débogage actif pour 24 heures.' : 'Mode de débogage arrêté.' });
      charger();
    } catch (e) {
      echec(e);
    }
  };

  return (
    <main className="ecran">
      <EnTeteRetour titre="L’assistant" retour="#/reglages" />

      <a href="#/entretien" style={{ color: 'inherit' }}>
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>Faisons connaissance</span>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {entretien ? `${entretien.assistant} vous connaît à ${entretien.score} %. ` : ''}Quelques questions par thème ; chacun peut être refait.
          </p>
        </Fenetre>
      </a>

      <a href="#/memoire" style={{ color: 'inherit' }}>
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>Ce qu’Alfred sait de vous</span>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Ses souvenirs à votre sujet : confirmer, corriger, oublier.</p>
        </Fenetre>
      </a>

      <a href="#/journal" style={{ color: 'inherit' }}>
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>Journal des actions</span>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Tout ce qu’Alfred a écrit pour vous, quand, pourquoi, et à partir de quoi.</p>
        </Fenetre>
      </a>

      {etat && (
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>Consommation</span>
          <p style={{ margin: 0, fontSize: 14 }}>
            {etat.disponible ? <>Modèle <span className="mono">{etat.modele}</span>.</> : 'Aucun assistant n’est configuré sur cette instance.'}
          </p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Ces 30 derniers jours : {etat.membre30j.demandes} demande(s), {etat.membre30j.jetons.toLocaleString('fr-FR')} jetons
            {etat.membre30j.demandes ? `, ${Math.round(etat.membre30j.dureeMoyenneMs / 1000)} s en moyenne` : ''}.
            Le foyer ce mois-ci : {etat.consommationMois.toLocaleString('fr-FR')} jetons{etat.plafondMois ? ` sur ${etat.plafondMois.toLocaleString('fr-FR')}` : ''}.
          </p>
        </Fenetre>
      )}

      {etat && moi.role === 'admin' && (
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>Mode de débogage</span>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Pendant 24 heures, les échanges du chat de tout le foyer sont écrits dans les journaux du serveur, pour comprendre une
            réponse étrange. Le reste du temps, aucun contenu n’y est gardé.
          </p>
          {etat.debogageJusquA
            ? <button className="btn" onClick={() => void debogage(false)}>Arrêter (actif jusqu’au {quand(etat.debogageJusquA)})</button>
            : <button className="btn" onClick={() => void debogage(true)}>Activer pour 24 heures</button>}
        </Fenetre>
      )}
    </main>
  );
}

const STATUTS: Record<string, string> = { en_attente: 'à valider', acceptee: 'validée', refusee: 'refusée', expiree: 'expirée' };

export function JournalActions({ actions }: { actions: Actions }) {
  const [lignes, setLignes] = useState<LigneJournal[] | null>(null);
  const echec = useCallback(echecDe(actions), [actions]);
  useEffect(() => {
    api.journal().then(setLignes).catch(echec);
  }, [echec]);

  return (
    <main className="ecran">
      <EnTeteRetour titre="Journal" retour="#/assistant" droite={lignes ? `${lignes.length}` : ''} />
      <p className="muted" style={{ margin: 0, fontSize: 13, textAlign: 'center' }}>Ce qu’Alfred a écrit pour vous, le plus récent d’abord.</p>
      {lignes === null && <p className="message muted">Chargement…</p>}
      {lignes?.length === 0 && <p className="message muted">Rien pour l’instant.</p>}
      {lignes && lignes.length > 0 && (
        <div className="dossier-notes">
          {lignes.map((l) => (
            <div key={l.id} className="journal-ligne">
              <span>{l.resume}</span>
              <small>{quand(l.le)} · {l.acteur}{l.demande ? ` · ${STATUTS[l.demande.statut ?? ''] ?? 'demande'}` : ''}</small>
              {l.raison && <small>Demandé : « {l.raison} »</small>}
              {l.suiteANonFiable && <small className="externe">Après la lecture d’un contenu externe (agenda partagé) : validation demandée.</small>}
              {l.erreur && <small className="externe">{l.erreur}</small>}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
